import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { IStoreSelectionPageProps, IStoreSelectionState } from './types'
import { getUserStores, clearUserStoresCache, hasTimeBoundAssignment } from '@api/userAndStoreContext'
import { mapStoresToStoreDetails } from './utils/storeMapper'
import { filterStores } from './utils/filterStores'
import Button from '@amzn/meridian/button'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { BACK_TO_LOGIN, STORE_LOAD_ERROR, STORE_NOT_AUTHORIZED_ERROR, SEARCH_PLACEHOLDER } from './constants'
import { useLogout } from '@hooks/useLogout'
import { getLocalDevConfig } from '@src/config/localDevConfig'
import { UnauthorizedException } from '@amzn/coral_com-amazon-jwostoremanagementgateway/UnauthorizedException'
import { setStoreList } from '@atredux/slices/storeSlice'
import {
  Header,
  StoreList,
  LoadingIndicator,
  ErrorMessage,
  SwitchStoreButton,
} from './components'
import Box from '@amzn/meridian/box'
import Column from '@amzn/meridian/column'
import SearchField from '@amzn/meridian/search-field'
import './StoreSelectionPage.scss'

/**
 * StoreSelectionPage Component
 *
 * Main container component that orchestrates the store selection functionality.
 * Manages state for stores, filtering, search, and selection.
 * Includes error handling, loading states, and empty state messages.
 */
export const StoreSelectionPage: React.FC<IStoreSelectionPageProps> = ({
  userId,
  onStoreSelected,
  selectionError,
  onRetrySelection,
  currentStoreId,
  selectedStoreId: initialSelectedStoreId,
  showSwitchButton,
  onSwitchStore,
  onClose,
}) => {
    const [state, setState] = useState<IStoreSelectionState>({
        stores: [],
        filteredStores: [],
        searchQuery: '',
        selectedStoreId: initialSelectedStoreId || null,
        isLoading: false,
        error: null,
        isErrorRetryable: true,
        isSwitchButtonEnabled: false,
    })

    const reduxDispatch = useDispatch()
    const [bundle] = useBundle(BUNDLE_NAME)

    // Fetch stores on component mount
    const loadStores = useCallback(async () => {
        setState((prev) => ({ ...prev, isLoading: true, error: null, isErrorRetryable: true }))

        try {
            // Force re-fetch for time-bound users to detect expired access (P467955166)
            if (hasTimeBoundAssignment()) {
                clearUserStoresCache()
            }
            const stores = mapStoresToStoreDetails(await getUserStores(userId))
                .sort((a, b) => (a.storeName || '').localeCompare(b.storeName || ''))
            reduxDispatch(setStoreList(stores))
            // Dev override: simulate no-store-access for P414214415 testing
            const { simulateNoStores } = getLocalDevConfig()
            if (simulateNoStores || stores.length === 0) {
                setState((prev) => ({
                    ...prev,
                    error: STORE_NOT_AUTHORIZED_ERROR,
                    isErrorRetryable: false,
                    isLoading: false,
                }))
                return
            }
            setState((prev) => ({
                ...prev,
                stores,
                filteredStores: stores,
                isLoading: false,
            }))
        } catch (error) {
            const isNotAuthorized = error instanceof UnauthorizedException
            setState((prev) => ({
                ...prev,
                error: isNotAuthorized ? STORE_NOT_AUTHORIZED_ERROR : STORE_LOAD_ERROR,
                isErrorRetryable: !isNotAuthorized,
                isLoading: false,
            }))
        }
    }, [userId])

    useEffect(() => {
        loadStores()
    }, [loadStores])

    // Handle retry after error
    const logout = useLogout()

    const handleRetry = () => {
        loadStores()
    }

    // Handle search query changes (typing only, no filtering)
    const handleSearchChange = (query: string) => {
        const filtered = filterStores(state.stores, query)
        setState((prev) => ({
            ...prev,
            searchQuery: query,
            filteredStores: filtered,
        }))
    }

    // Handle search submit (filter stores when button clicked)
    const handleSearchSubmit = () => {
        (document.activeElement as HTMLElement)?.blur()
        const filtered = filterStores(state.stores, state.searchQuery)
        setState((prev) => ({
            ...prev,
            filteredStores: filtered,
        }))
    }

    // Handle search clear (reset query and show all stores)
    const handleSearchClear = () => {
        setState((prev) => ({
            ...prev,
            searchQuery: '',
            filteredStores: prev.stores,
        }))
    }

    // Handle store selection
    const handleStoreClick = (storeId: string) => {
        // Update selected store ID in state
        setState((prev) => ({
            ...prev,
            selectedStoreId: storeId,
        }))

        // Two modes of operation in the Store Selection Sheet/list of selectable stores:
        // - Initial selection (showSwitchButton=false): immediately call onStoreSelected
        // - Subsequent selection (showSwitchButton=true): update state only, user must click "Switch store" button
        if (!showSwitchButton) {
            onStoreSelected(storeId)
        }
    }

    // Handle switch store button click
    const handleSwitchStoreClick = () => {
        if (state.selectedStoreId && onSwitchStore) {
            onSwitchStore(state.selectedStoreId)
        }
    }

    // Update button enabled state when selection or currentStoreId changes
    useEffect(() => {
        if (showSwitchButton) {
            // Enable button when a store is selected
            const shouldEnable = state.selectedStoreId !== null
            setState((prev) => ({
                ...prev,
                isSwitchButtonEnabled: shouldEnable,
            }))
        }
    }, [state.selectedStoreId, showSwitchButton])

    return (
    <Column height="100%" spacing="none">
      <Column spacingInset="300 400 none 400">
        <Header onClose={onClose} />
      </Column>

      {/* Show loading indicator during API call */}
      {state.isLoading && <LoadingIndicator />}

      {/* Show error message with retry button on API failure */}
      {state.error && (
        <>
          <ErrorMessage messageStringId={state.error} onRetry={state.isErrorRetryable ? handleRetry : undefined} />
          {/* P414214415: Show logout button when user has no store access */}
          {!state.isErrorRetryable && (
            <div className="no-store-logout-container">
              <Button type="primary" size="large" onClick={logout} data-testid="no-store-logout">
                {t(bundle, BACK_TO_LOGIN)}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Show error message for store selection failure */}
      {selectionError && onRetrySelection && (
        <ErrorMessage messageStringId={selectionError} onRetry={onRetrySelection} />
      )}

      {/* Show main content when not loading and no error */}
      {!state.isLoading && !state.error && !selectionError && (
        <>
          <Column spacingInset="300 400 450 400">
            <Box type="fill" spacingInset="300 400">
              <SearchField
                value={state.searchQuery}
                onChange={handleSearchChange}
                onSubmit={handleSearchSubmit}
                onClear={handleSearchClear}
                placeholder={t(bundle, SEARCH_PLACEHOLDER)}
                searchButton={true}
                data-atb-id="store-search-input"
              />
            </Box>
          </Column>
          <div className="store-selection-page-container">
            <StoreList
              stores={state.filteredStores}
              onStoreClick={handleStoreClick}
              currentStoreId={currentStoreId}
              selectedStoreId={state.selectedStoreId}
            />
          </div>
          {/* Render switch button when in display button mode */}
          {showSwitchButton && (
            <SwitchStoreButton
              enabled={state.isSwitchButtonEnabled}
              onClick={handleSwitchStoreClick}
            />
          )}
        </>
      )}
    </Column>
    )
}
